import { type RefObject, useCallback, useState } from 'react';

import { getTranscriptElementFromCharIndex } from '../components/transcript/utils';
import { saveClipFromTranscript } from '../services/AIClipProducer';
import { useEditor } from '../stores-v2';
import { SidebarOption } from '../types.ts/general';
import { AspectRatio } from '../types.ts/video';
import { extractErrorInfo } from '../utility/error';
import { LogManager } from '../utility/Logger';
import {
  getClosestElementIndexToLeftByFilter,
  getClosestElementIndexToRightByFilter,
  getClosestNotRemovedElementIndexToLeft,
  getClosestNotRemovedElementIndexToRight,
  getClosestNotRemovedTextIndexToLeft,
  getClosestNotRemovedTextIndexToRight,
  getClosestRemovedIndexToLeft,
  getClosestRemovedIndexToRight,
} from '../videoTranscriptionProcessor/utils';
import useAddMediaToTimeline from './useAddMediaToTimeline';
import { useKaraoke } from './useKaraoke';

import type { TranscriptElementBlock } from '../components/transcript/utils';
import type { KaraokeLine } from '../editor/types/karaoke';
import type { IVideoCreatorStore } from '../stores/IVideoCreatorStore';
import type {
  ImageKey,
  ImageWithType,
  TranscriptContextMenuActions,
} from '../types.ts/general';
import type { TranscriptElement } from '../types.ts/transcript';

// Selection boundary shape (legacy index + optional segment addressing)
interface SelectionBoundaries {
  startIndex: number;
  endIndex: number;
  focusIndex?: number;
  anchorIndex?: number;
}

const logger = LogManager.getLogger('hook:useTranscriptOperations');

export const useTranscriptOperations = (
  videoCreator: IVideoCreatorStore,
  transcriptElements: TranscriptElement[] | undefined,
  isSubtitlesTranslated: boolean,
  showHistory: boolean,
  transcriptBlocks: TranscriptElementBlock[],
  karaokeLines: KaraokeLine[] | undefined,
) => {
  const editorStore = useEditor();
  const { addMediaToTimeline } = useAddMediaToTimeline();

  const [selectedWordIndex, setSelectedWordIndex] = useState<number>(-1);
  const useNewKaraoke = editorStore.useNewKaraoke && !isSubtitlesTranslated;
  const useNewTranscript = editorStore.useTranscriptSegments;

  const {
    commandAddBreak,
    commandRemoveBreak,
    commandHideInKaraoke,
    commandShowInKaraoke,
  } = useKaraoke(editorStore.editor);

  const compensateForRemovedElementsPosition = useCallback(
    (index: number) => {
      if (showHistory) return index;
      if (!transcriptElements) return 0;
      let i = 0;
      while (index > 0 && i < transcriptElements.length) {
        if (
          transcriptElements[i].state !== 'removed' &&
          transcriptElements[i].state !== 'cut'
        ) {
          index--;
        }
        i++;
      }
      return i;
    },
    [showHistory, transcriptElements],
  );

  const getBoundaryIndexesFromSelectionInDiff = useCallback(
    (selection: Selection) => {
      const anchorBlockIndex = +(
        selection.anchorNode?.parentElement?.dataset.blockIndex || 0
      );
      const focusBlockIndex = +(
        selection.focusNode?.parentElement?.dataset.blockIndex || 0
      );
      const [startBlockIndex, endBlockIndex] = [
        Math.min(anchorBlockIndex, focusBlockIndex),
        Math.max(anchorBlockIndex, focusBlockIndex),
      ];
      const startBlock = transcriptBlocks.find(
        block => block.indexOffset === startBlockIndex,
      );
      const endBlock = transcriptBlocks.find(
        block => block.indexOffset === endBlockIndex,
      );
      const [startOffset, endOffset] =
        anchorBlockIndex === focusBlockIndex
          ? [selection.anchorOffset, selection.focusOffset].sort(
              (a, b) => a - b,
            )
          : anchorBlockIndex < focusBlockIndex
            ? [selection.anchorOffset, selection.focusOffset]
            : [selection.focusOffset, selection.anchorOffset];
      const startElement = getTranscriptElementFromCharIndex(
        startOffset,
        startBlock?.elements,
      );
      const endElement = getTranscriptElementFromCharIndex(
        endOffset,
        endBlock?.elements,
      );
      if (startElement && endElement) {
        return {
          startIndex: startBlockIndex + startElement.elementIndex,
          endIndex: endBlockIndex + endElement.elementIndex,
        };
      }
      return null;
    },
    [transcriptBlocks],
  );

  // Legacy: Index-based boundary extraction for VideoCreatorStore
  const getIndexBoundariesFromSelection = useCallback(
    (
      selection: Selection,
      scrollRef: RefObject<HTMLDivElement>,
    ): SelectionBoundaries | null => {
      const anchorIndex: number =
        selection?.anchorNode?.parentElement === scrollRef.current
          ? compensateForRemovedElementsPosition(selection.anchorOffset) - 2
          : Number(
              selection.anchorNode?.parentElement?.dataset?.index ||
                (selection.anchorNode as HTMLElement)?.dataset?.index,
            );
      const focusIndex: number =
        selection?.focusNode?.parentElement === scrollRef.current
          ? compensateForRemovedElementsPosition(selection?.focusOffset) - 2
          : Number(
              selection?.focusNode?.parentElement?.dataset?.index ||
                (selection.focusNode as HTMLElement)?.dataset?.index,
            );

      if (isNaN(anchorIndex) || isNaN(focusIndex)) return null;

      let startIndex, endIndex, startOffset;
      if (anchorIndex >= focusIndex) {
        startIndex = focusIndex;
        endIndex = anchorIndex;
        startOffset = selection.focusOffset;
      } else {
        startIndex = anchorIndex;
        endIndex = focusIndex;
        startOffset = selection.anchorOffset;
      }

      if (selection?.toString()?.length) {
        if (
          transcriptElements &&
          transcriptElements[startIndex]?.type === 'punct' &&
          startOffset
        ) {
          startIndex += 1;
        }
      }

      return {
        startIndex,
        endIndex,
        focusIndex,
        anchorIndex,
      };
    },
    [transcriptElements, compensateForRemovedElementsPosition],
  );

  const getBoundaryIndexesFromSelection = useCallback(
    (selection: Selection, scrollRef?: RefObject<HTMLDivElement>) => {
      if (showHistory) return getBoundaryIndexesFromSelectionInDiff(selection);
      else return getIndexBoundariesFromSelection(selection, scrollRef!);
    },
    [
      showHistory,
      getBoundaryIndexesFromSelectionInDiff,
      getIndexBoundariesFromSelection,
    ],
  );

  const getStartAndEndOfSelection = useCallback(
    (scrollRef?: RefObject<HTMLDivElement>) => {
      const selection = window.getSelection();
      if (!(selection && selection.toString().length)) return null;
      const boundaries = getBoundaryIndexesFromSelection(selection, scrollRef);
      if (!boundaries) {
        selection.empty();
      }
      return boundaries;
    },
    [getBoundaryIndexesFromSelection],
  );

  const convertIndexToSegmentBoundaries = useCallback(
    (indexBoundaries: SelectionBoundaries) => {
      let { startIndex, endIndex } = indexBoundaries;

      if (!transcriptElements) {
        return null;
      }

      // Look up transcript elements at start and end positions to get segment data
      let startElement = transcriptElements[startIndex];
      let endElement = transcriptElements[endIndex];

      // If start element has whitespace subtype, find closest non-whitespace to the right
      if (startElement?.subtype === 'whitespace') {
        const nonWhitespaceStartIndex = getClosestElementIndexToRightByFilter(
          startIndex,
          transcriptElements,
          el => el.subtype !== 'whitespace',
        );

        if (nonWhitespaceStartIndex !== -1) {
          startIndex = nonWhitespaceStartIndex;
          startElement = transcriptElements[startIndex];
        }
      }

      // If end element has whitespace subtype, find closest non-whitespace to the left
      if (endElement?.subtype === 'whitespace') {
        const nonWhitespaceEndIndex = getClosestElementIndexToLeftByFilter(
          endIndex,
          transcriptElements,
          el => el.subtype !== 'whitespace',
        );

        if (nonWhitespaceEndIndex !== -1) {
          endIndex = nonWhitespaceEndIndex;
          endElement = transcriptElements[endIndex];
        }
      }

      // Validate that ranges are still valid after adjustment
      if (startIndex > endIndex) {
        console.warn(
          `Invalid range after whitespace adjustment: startIndex (${startIndex}) > endIndex (${endIndex})`,
        );
        return null;
      }

      // Both elements must have segment data - clip_segment_id and clip_segment_position properties;
      if (
        !('clip_segment_id' in startElement) ||
        !('clip_segment_id' in endElement) ||
        !startElement.clip_segment_id ||
        !endElement.clip_segment_id ||
        startElement.clip_segment_position == null ||
        endElement.clip_segment_position == null
      ) {
        return null;
      }

      return {
        fromSegmentId: startElement.clip_segment_id,
        fromIndex: startElement.clip_segment_position,
        toSegmentId: endElement.clip_segment_id,
        toIndex: endElement.clip_segment_position,
      };
    },
    [transcriptElements],
  );

  // New: Segment-based boundary extraction for VideoEditor commands
  // Reuses proven getBoundaryIndexesFromSelection logic and converts indexes to segment data
  const getSegmentBoundariesFromSelection = useCallback(
    (
      selection: Selection,
      scrollRef: RefObject<HTMLDivElement>,
    ): {
      fromSegmentId: string;
      fromIndex: number;
      toSegmentId: string;
      toIndex: number;
    } | null => {
      // First, get the index-based boundaries using the proven logic
      // This correctly delegates to getBoundaryIndexesFromSelectionInDiff when showHistory is true
      const indexBoundaries = getBoundaryIndexesFromSelection(
        selection,
        scrollRef,
      );
      if (!indexBoundaries || !transcriptElements) {
        return null;
      }
      return convertIndexToSegmentBoundaries(indexBoundaries);
    },
    [
      getBoundaryIndexesFromSelection,
      convertIndexToSegmentBoundaries,
      transcriptElements,
    ],
  );

  const handleCursorMove = useCallback(
    (
      direction: 1 | -1,
      cursor: { from: number; to: number },
      setCursor: (cursor: { from: number; to: number }) => void,
    ) => {
      if (!transcriptElements) return;
      let newPosition;
      if (direction > 0) {
        newPosition = getClosestNotRemovedElementIndexToRight(
          cursor.from + 1,
          transcriptElements,
        );
        if (newPosition === -1) {
          newPosition =
            cursor.from + 1 > transcriptElements.length
              ? 0
              : transcriptElements.length;
        }
      } else {
        newPosition = getClosestNotRemovedElementIndexToLeft(
          cursor.from - 1,
          transcriptElements,
        );
        newPosition =
          newPosition === -1 ? transcriptElements.length : newPosition;
      }

      setCursor({
        from: newPosition || 0,
        to: (newPosition || 0) + 1,
      });

      const newPositionElement = transcriptElements[newPosition];

      if (
        newPositionElement &&
        'ts' in newPositionElement &&
        newPositionElement.ts != null
      ) {
        const moveToTs = Math.ceil((newPositionElement.ts || 0) * 1000) / 1000;
        videoCreator.setTime(moveToTs, true);
      }
    },
    [transcriptElements, videoCreator],
  );

  const handleAddPunctuation = useCallback(
    (
      code: 'Comma' | 'Period' | 'Space' | 'Enter',
      cursor: { from: number; to: number },
      setCursor: (cursor: { from: number; to: number }) => void,
    ) => {
      if (editorStore.useTranscriptSegments && !isSubtitlesTranslated) {
        if (!transcriptElements) return;
        const punctAfterIndex = getClosestElementIndexToLeftByFilter(
          cursor.from - 1,
          transcriptElements,
          el =>
            el.subtype !== 'whitespace' &&
            el.state !== 'removed' &&
            el.state !== 'cut' &&
            !!el.id,
        );
        if (punctAfterIndex === -1) {
          logger.error(
            "handleAddPunctuation: couldn't find index to add after",
            {
              cursor,
            },
          );
          return;
        }
        const punctAfterElement = transcriptElements[punctAfterIndex];
        if (
          !punctAfterElement ||
          punctAfterElement.clip_segment_id == null ||
          punctAfterElement.clip_segment_position == null
        ) {
          logger.error(
            "handleAddPunctuation: couldn't find element to add after",
            {
              cursor,
              punctAfterIndex,
              punctAfterElement,
            },
          );
          return;
        }
        editorStore.editor.commandAddTranscriptPunctuation(
          code,
          punctAfterElement.clip_segment_id,
          punctAfterElement.clip_segment_position,
        );
        if (code === 'Enter') {
          return; // don't change cursor because newline will take whitespace's place
        }
      } else {
        videoCreator.addPunctuation(code, cursor.from, isSubtitlesTranslated);
      }
      setCursor({
        from: cursor.from + 1,
        to: cursor.from + 2,
      });
    },
    [
      videoCreator,
      isSubtitlesTranslated,
      editorStore.useTranscriptSegments,
      transcriptElements,
    ],
  );

  const handleAddKaraokeBreak = useCallback(
    (cursor: { from: number; to: number }) => {
      if (useNewKaraoke) {
        if (!transcriptElements) return;
        const breakAfterIndex = getClosestElementIndexToLeftByFilter(
          cursor.from - 1,
          transcriptElements,
          el =>
            el.subtype !== 'whitespace' &&
            el.state !== 'removed' &&
            el.state !== 'cut' &&
            !!el.id,
        );
        if (breakAfterIndex === -1) return;
        const breakAfterElement = transcriptElements[breakAfterIndex];
        if (!breakAfterElement?.id) return;
        commandAddBreak(breakAfterElement.id);
        return;
      }

      let position = getClosestNotRemovedElementIndexToLeft(
        cursor.from - 1,
        transcriptElements!,
      );

      const closeBreak = videoCreator.hasKaraokeBreakNear(
        cursor.from,
        isSubtitlesTranslated,
      );

      if (
        position < 0 ||
        position > transcriptElements!.length - 1 ||
        closeBreak
      )
        return;

      videoCreator.addKaraokeBreaks([position], isSubtitlesTranslated);
    },
    [transcriptElements, videoCreator, isSubtitlesTranslated, useNewKaraoke],
  );

  const handleRemoveKaraokeBreak = useCallback(
    (position: number) => {
      if (
        position < 0 ||
        !transcriptElements ||
        position > transcriptElements.length - 1
      )
        return;

      if (useNewKaraoke) {
        const restoreAfterIndex = getClosestElementIndexToLeftByFilter(
          position,
          transcriptElements,
          el =>
            el.subtype !== 'whitespace' &&
            el.state !== 'removed' &&
            el.state !== 'cut' &&
            !!el.id,
        );
        if (restoreAfterIndex === -1) return;
        const restoreAfterElement = transcriptElements[restoreAfterIndex];
        if (!restoreAfterElement?.id) return;
        commandRemoveBreak(restoreAfterElement.id);
        return;
      }

      videoCreator.removeKaraokeBreak(position, isSubtitlesTranslated);
    },
    [transcriptElements, videoCreator, isSubtitlesTranslated, useNewKaraoke],
  );

  const extendEndIndexToFollowingWhitespace = useCallback(
    (endIndex: number): number => {
      if (!transcriptElements) {
        return endIndex;
      }
      const nextElIndex = getClosestNotRemovedElementIndexToRight(
        endIndex + 1,
        transcriptElements,
      );
      if (nextElIndex !== -1 && transcriptElements[nextElIndex].value === ' ') {
        return nextElIndex;
      } else {
        return endIndex;
      }
    },
    [transcriptElements],
  );

  const handleTranscriptRestore = useCallback(
    async (
      action: 'restore' | 'showKaraoke',
      scrollRef?: RefObject<HTMLDivElement>,
      setCursor?: (cursor: { from: number; to: number }) => void,
    ) => {
      if (videoCreator.isPlaying) return;
      if (!transcriptElements) return;

      // Check if we should use new VideoEditor architecture
      const editorStore = videoCreator.rootStore?.editorStore;
      const useNewArchitecture = editorStore?.useTranscriptSegments;

      // LEGACY: Index-based path with VideoCreatorStore
      let startIndex, endIndex;
      const boundaries = getStartAndEndOfSelection(scrollRef);
      if (boundaries) {
        ({ startIndex, endIndex } = boundaries);
        startIndex = getClosestRemovedIndexToRight(
          startIndex,
          transcriptElements,
        );
        endIndex = getClosestRemovedIndexToLeft(endIndex, transcriptElements);
        if (startIndex === -1 || endIndex === -1 || endIndex < startIndex)
          return;
      } else if (selectedWordIndex > -1) {
        endIndex = startIndex = selectedWordIndex;
        if (
          action === 'showKaraoke' &&
          transcriptElements[startIndex].type === 'text'
        ) {
          endIndex = extendEndIndexToFollowingWhitespace(endIndex);
        }
      } else {
        return;
      }

      if (action === 'showKaraoke' && useNewKaraoke) {
        const tokenIds: string[] = [];
        for (let i = startIndex; i <= endIndex; i++) {
          const el = transcriptElements[i];
          if (
            el.subtype !== 'whitespace' &&
            el.state !== 'removed' &&
            el.state !== 'cut' &&
            el.id
          ) {
            tokenIds.push(el.id);
          }
        }
        if (tokenIds.length) {
          commandShowInKaraoke(tokenIds);
        }
        return;
      }

      if (
        action !== 'showKaraoke' &&
        useNewArchitecture &&
        editorStore?.editor?.commandTranscriptRestore
      ) {
        // NEW: VideoEditor path with segment-based boundaries (Not for karaoke yet)
        const segmentBoundaries = convertIndexToSegmentBoundaries({
          startIndex,
          endIndex,
        });
        if (segmentBoundaries) {
          await editorStore.editor.commandTranscriptRestore(
            segmentBoundaries.fromSegmentId,
            segmentBoundaries.fromIndex,
            segmentBoundaries.toSegmentId,
            segmentBoundaries.toIndex,
          );
          return;
        }
        return;
        // NEW: EARLY EXIT
      }

      // LEGACY: EXECUTE RESTORE
      await videoCreator.restoreTranscriptAndVideo(
        startIndex,
        endIndex! + 1,
        isSubtitlesTranslated,
        !!boundaries && action == 'showKaraoke',
      );

      if (setCursor) {
        setCursor({
          from: endIndex,
          to: endIndex + 1,
        });
      }

      await videoCreator.resetPhotoHighlights();
      videoCreator.recalculateTimelineScale();
    },
    [
      transcriptElements,
      selectedWordIndex,
      isSubtitlesTranslated,
      videoCreator,
      getStartAndEndOfSelection,
      getSegmentBoundariesFromSelection,
      extendEndIndexToFollowingWhitespace,
      useNewKaraoke,
    ],
  );

  const getSelectedStartEndIndex = useCallback(
    (scrollRef?: RefObject<HTMLDivElement>) => {
      let startIndex, endIndex;
      const boundaries = getStartAndEndOfSelection(scrollRef);
      if (boundaries) {
        ({ startIndex, endIndex } = boundaries);
      } else if (selectedWordIndex > -1) {
        endIndex = startIndex = selectedWordIndex;
      } else {
        return null;
      }

      startIndex = Math.max(startIndex, 0);
      endIndex = Math.min(endIndex, transcriptElements!.length - 1);
      return { startIndex, endIndex };
    },
    [getStartAndEndOfSelection, selectedWordIndex, transcriptElements],
  );

  const handleHideKaraoke = useCallback(
    (scrollRef?: RefObject<HTMLDivElement>) => {
      if (!transcriptElements) return;
      let { startIndex, endIndex } = getSelectedStartEndIndex(scrollRef) || {};
      if (startIndex === undefined || endIndex === undefined) return;
      if (useNewKaraoke) {
        const tokenIds = [];
        for (let i = startIndex; i <= endIndex; i++) {
          const el = transcriptElements[i];
          if (
            el.subtype !== 'whitespace' &&
            el.state !== 'removed' &&
            el.state !== 'cut' &&
            !!el.id
          ) {
            tokenIds.push(el.id);
          }
        }
        if (tokenIds.length) {
          commandHideInKaraoke(tokenIds);
        }
        return;
      } else {
        if (
          startIndex === endIndex &&
          transcriptElements[endIndex].type === 'text'
        ) {
          endIndex = extendEndIndexToFollowingWhitespace(endIndex);
        }
        videoCreator.hideKaraoke(
          { startIndex, endIndex },
          isSubtitlesTranslated,
        );
      }
      // FIXME: NEXT Do I need to call this for new flow?
      setSelectedWordIndex(-1);
    },
    [
      transcriptElements,
      getSelectedStartEndIndex,
      extendEndIndexToFollowingWhitespace,
      videoCreator,
      isSubtitlesTranslated,
      useNewKaraoke,
    ],
  );

  const handleTranscriptDelete = useCallback(
    (
      cursor: { from: number; to: number },
      setCursor: (cursor: { from: number; to: number }) => void,
      setLastTimeUpdated: (time: number) => void,
      scrollRef?: RefObject<HTMLDivElement>,
      isInKaraokeView?: boolean,
      toClipboard: boolean = false,
    ) => {
      if (videoCreator.isPlaying) return;
      if (!transcriptElements) return;

      // Check if we should use new VideoEditor architecture
      const editorStore = videoCreator.rootStore?.editorStore;
      const editor = editorStore?.editor;
      const useNewArchitecture = editorStore?.useTranscriptSegments;

      // LEGACY: Index-based
      let startIndex: number, endIndex: number;
      const boundaries = getStartAndEndOfSelection(scrollRef);
      if (boundaries) {
        ({ startIndex, endIndex } = boundaries);
      } else if (selectedWordIndex > 0) {
        endIndex = startIndex = getClosestNotRemovedElementIndexToLeft(
          selectedWordIndex,
          transcriptElements,
        );
      } else if (cursor.to > 0) {
        endIndex = startIndex = getClosestElementIndexToLeftByFilter(
          cursor.from - 1,
          transcriptElements,
          el => {
            return (
              el.state !== 'removed' &&
              el.state !== 'cut' &&
              !!el.value &&
              (el.karaoke_break || !isInKaraokeView || el.value !== '\n')
            );
          },
        );
        if (transcriptElements![startIndex].karaoke_break) {
          handleRemoveKaraokeBreak(startIndex);
          if (
            isInKaraokeView ||
            transcriptElements![startIndex].value !== '\n'
          ) {
            return;
          }
        }
      } else {
        return;
      }

      if (useNewArchitecture) {
        // NEW: VideoEditor path with segment-based boundaries
        // Convert legacy index-based to segment-based boundaries
        const selection = window.getSelection();
        if (selection) {
          const segmentBoundaries = convertIndexToSegmentBoundaries({
            startIndex,
            endIndex,
          });
          if (segmentBoundaries) {
            editor.commandRemoveTranscript(
              segmentBoundaries.fromSegmentId,
              segmentBoundaries.fromIndex,
              segmentBoundaries.toSegmentId,
              segmentBoundaries.toIndex,
              toClipboard,
            );
            // VideoEditor handles timeline updates internally
            return;
          } else if (startIndex === endIndex && startIndex > 0) {
            if (isInKaraokeView) handleRemoveKaraokeBreak(startIndex);
            handleCursorMove(-1, cursor, setCursor);
            return;
          }
        }
        return;
        // NEW: EXIT EARLY
      }

      // LEGACY: EXECUTE DELETE
      ((): Promise<void> => {
        return videoCreator.cutTranscriptAndVideo(
          startIndex,
          endIndex! + 1,
          true,
          isSubtitlesTranslated,
        );
      })().then(() => {
        let newPosition = isSubtitlesTranslated
          ? Math.min(endIndex, transcriptElements!.length)
          : Math.min(
              getClosestNotRemovedElementIndexToRight(
                endIndex + 1,
                transcriptElements!,
              ),
              transcriptElements!.length,
            );

        if (newPosition < 0) {
          newPosition = transcriptElements!.length;
        }

        setCursor({
          from: newPosition,
          to: newPosition + 1,
        });

        if (newPosition > 0) {
          const newTextPosition = getClosestNotRemovedTextIndexToRight(
            newPosition,
            transcriptElements!,
          );
          const newPositionElement = transcriptElements?.at(newTextPosition);
          if (newPositionElement && 'ts' in newPositionElement) {
            const newTime = newPositionElement.ts || 0;
            videoCreator.setTime(newTime, true);
            setLastTimeUpdated(newTime);
          }
        }
        videoCreator.recalculateTimelineScale();
      });
    },
    [
      transcriptElements,
      selectedWordIndex,
      isSubtitlesTranslated,
      videoCreator,
      getStartAndEndOfSelection,
      handleRemoveKaraokeBreak,
    ],
  );

  const getCursorPosition = useCallback(() => {
    const currentWordIndex = transcriptElements!.findIndex(
      el =>
        el.type === 'text' &&
        el.state !== 'removed' &&
        el.state !== 'cut' &&
        el.value &&
        'end_ts' in el &&
        el.end_ts &&
        el.end_ts > videoCreator.time,
    );

    return {
      from: currentWordIndex,
      to: currentWordIndex + 1,
    };
  }, [transcriptElements, videoCreator.time]);

  const skipToWordWithIndex = useCallback(
    (
      index: number,
      setCursor: (cursor: { from: number; to: number }) => void,
      setAutoScroll: (autoScroll: boolean) => void,
    ) => {
      if (!transcriptElements?.at(index)) return;
      if (
        transcriptElements.at(index)?.state === 'removed' ||
        transcriptElements.at(index)?.state === 'cut'
      ) {
        return;
      }
      const wasPlaying = videoCreator.isPlaying;
      let element = transcriptElements[index];
      if (element.type !== 'text') {
        element =
          transcriptElements[
            getClosestNotRemovedTextIndexToRight(index, transcriptElements)
          ] ??
          transcriptElements[
            getClosestNotRemovedTextIndexToLeft(index, transcriptElements)
          ];
      }

      if ('ts' in element) {
        videoCreator.setTime(element.ts!, true).then(() => {
          if (wasPlaying) {
            videoCreator.renderer?.play();
          }
          setCursor({
            from: index,
            to: index + 1,
          });
          setAutoScroll(true);
        });
      }
    },
    [transcriptElements, videoCreator],
  );

  const getActions = useCallback(
    (
      wordIndex: number,
      selection: Selection | null = null,
      scrollRef?: RefObject<HTMLDivElement>,
    ): TranscriptContextMenuActions[] => {
      let clipboardNotEmpty = false;
      const editorStore = videoCreator.rootStore?.editorStore;
      const editor = editorStore?.editor;
      if (editorStore?.useTranscriptSegments) {
        clipboardNotEmpty = editor.getClipboardSegments().length > 0;
      } else {
        clipboardNotEmpty =
          !!videoCreator.videoTranscriptionProcessor.transcriptClipboard;
      }
      if (wordIndex >= transcriptElements!.length) {
        if (clipboardNotEmpty) {
          return ['pasteTextBefore'];
        }
        return [];
      }
      const selectedItem = transcriptElements![wordIndex];

      if (selection && selection.toString().length) {
        const { startIndex, endIndex } =
          getBoundaryIndexesFromSelection(selection, scrollRef) || {};
        if (
          showHistory &&
          startIndex !== undefined &&
          endIndex !== undefined &&
          transcriptElements!
            .slice(startIndex, endIndex)
            .some(el => el.state === 'removed' || el.state === 'cut')
        ) {
          return ['restore'];
        }
      }

      if (selectedItem.state === 'removed' || selectedItem.state === 'cut')
        return ['restore'];
      const menuActions = [] as TranscriptContextMenuActions[];
      if ((selection?.toString()?.split(' ') || []).length <= 5) {
        menuActions.push('replace');
        if (clipboardNotEmpty) {
          menuActions.push('pasteTextBefore');
        }
      }

      if (editorStore.useNewKaraoke) {
        const { id } = selectedItem;
        menuActions.push(
          karaokeLines &&
            id &&
            karaokeLines.some(
              line => line?.allHiddenTranscriptTokenIds?.includes(id),
            )
            ? 'showKaraoke'
            : 'hideKaraoke',
        );
      } else {
        menuActions.push(
          selectedItem.state === 'muted' ? 'showKaraoke' : 'hideKaraoke',
        );
      }

      if (selectedItem.photo_highlight_id) {
        return videoCreator.punchListManager.addedPunchListItemId
          ? [...menuActions, 'removeTextLoading', 'removePhotoLoading']
          : [...menuActions, 'removeText', 'removePhoto'];
      }

      const finalActions = [
        ...menuActions,
        'removeText',
        'cutText',
        'saveAsClip',
        videoCreator.punchListManager.addedPunchListItemId
          ? 'addPhotoLoading'
          : 'addPhoto',
        'editTiming',
      ];

      return finalActions as TranscriptContextMenuActions[];
    },
    [
      transcriptElements,
      videoCreator,
      getBoundaryIndexesFromSelection,
      showHistory,
      editorStore.enableNewKaraokeArchitecture,
    ],
  );

  const handleRemovePhoto = useCallback(async () => {
    if (videoCreator.punchListManager.addedPunchListItemId) return;
    const punchListId =
      transcriptElements![selectedWordIndex!].photo_highlight_id;
    if (!punchListId) return;
    videoCreator.punchListManager.removePunchListItem(punchListId);
    await videoCreator.deleteElementWithTranscription(punchListId);
  }, [transcriptElements, selectedWordIndex, videoCreator]);

  const handleAddPhoto = useCallback(
    async (
      photoSelection: {
        loc: Record<'startIndex' | 'endIndex', number>;
        text: string;
      },
      selectedImage: ImageWithType[ImageKey],
    ) => {
      if (!photoSelection || !selectedImage?.url) {
        return;
      }
      if (!photoSelection.loc) return;

      let startIndex = getClosestNotRemovedTextIndexToRight(
        photoSelection.loc.startIndex,
        transcriptElements!,
      );

      let endIndex = getClosestNotRemovedTextIndexToLeft(
        photoSelection.loc.endIndex,
        transcriptElements!,
      );
      if (startIndex === -1 || endIndex === -1 || endIndex < startIndex) return;

      const startElement = transcriptElements?.at(startIndex);
      const endElement = transcriptElements?.at(endIndex);

      if (
        !startElement ||
        !endElement ||
        !('ts' in startElement) ||
        !('end_ts' in endElement)
      )
        return;

      const startTime = startElement.ts!;
      const endTime = endElement.end_ts!;
      videoCreator.sidebarOptions = SidebarOption.aiProducer;

      const aspectRatio = +selectedImage.width / +selectedImage.height;

      const punchListItem = videoCreator.punchListManager.addPhotoToPunchList(
        photoSelection.text,
        selectedImage.url,
        selectedImage.description || '',
        selectedImage.type,
        startIndex!,
        endIndex!,
        aspectRatio,
      );
      try {
        const duration = endTime - startTime;
        const maxTrack = videoCreator.getMaxTrack();
        const currTrack = maxTrack + 1;
        const freeTrack = videoCreator.getFreeMediaTrack('image', duration);
        const punchListTrack = freeTrack || currTrack;
        const isPortrait = aspectRatio < 1;
        const videoAspectRatio =
          videoCreator.currentVideo?.aspectRatio ||
          videoCreator.getDefaultAspectRatio();

        await addMediaToTimeline(
          {
            id: punchListItem!.id!,
            src: punchListItem!.artifactSrc!,
            description: selectedImage.description || '',
            aspectRatio,
            alt: selectedImage.alt || '',
            type: punchListItem.type as 'artifact' | 'stock' | 'ai',
          },
          'photo',
          {
            time: startTime,
            duration,
            track: punchListTrack,
            useComposition:
              isPortrait && videoAspectRatio === AspectRatio.AR_16_9,
            id: punchListItem!.id!,
          },
        );

        videoCreator.videoTranscriptionProcessor.addPhotoHighlight(
          startIndex,
          endIndex,
          punchListItem!.id!,
        );
      } catch (error) {
        console.error('Error adding photo to timeline:', error);
        videoCreator.punchListManager.removePunchListItem(punchListItem!.id!);
      }

      setSelectedWordIndex(-1);
    },
    [transcriptElements, videoCreator, addMediaToTimeline],
  );

  const handleCutText = useCallback(
    async (
      cursor: { from: number; to: number },
      setCursor: (cursor: { from: number; to: number }) => void,
      setLastTimeUpdated: (time: number) => void,
      scrollRef?: RefObject<HTMLDivElement>,
      isInKaraokeView?: boolean,
    ) => {
      // Check if we should use new VideoEditor architecture
      const editorStore = videoCreator.rootStore?.editorStore;
      const useNewArchitecture = editorStore?.useTranscriptSegments;

      if (useNewArchitecture) {
        handleTranscriptDelete(
          cursor,
          setCursor,
          setLastTimeUpdated,
          scrollRef,
          isInKaraokeView,
          true,
        );
      } else {
        const { startIndex, endIndex } =
          getSelectedStartEndIndex(scrollRef) || {};
        if (startIndex === undefined || endIndex === undefined) return;
        await videoCreator.cutSentence(startIndex, endIndex);
      }
      videoCreator.recalculateTimelineScale();
    },
    [getSelectedStartEndIndex, videoCreator],
  );

  const handlePasteText = useCallback(
    (after: boolean, scrollRef?: RefObject<HTMLDivElement>) => {
      const { startIndex, endIndex } =
        getSelectedStartEndIndex(scrollRef) || {};
      if (startIndex === undefined || endIndex === undefined) return;

      // Check if we should use new VideoEditor architecture
      const editorStore = videoCreator.rootStore?.editorStore;
      const editor = editorStore?.editor;

      if (editorStore?.useTranscriptSegments) {
        const segmentBoundaries = convertIndexToSegmentBoundaries({
          startIndex: startIndex,
          endIndex: startIndex,
        });
        if (segmentBoundaries) {
          editor.commandPasteTranscript(
            segmentBoundaries?.fromSegmentId,
            segmentBoundaries?.fromIndex,
            after,
          );
        }
        return;
      }
      // Legacy: Use VideoCreatorStore for paste
      const intoPosition = after ? startIndex + 1 : startIndex;
      videoCreator.pasteSentence(intoPosition);
    },
    [getSelectedStartEndIndex, videoCreator],
  );

  const handleSaveAsClip = useCallback(
    async (scrollRef?: RefObject<HTMLDivElement>) => {
      const selection = window.getSelection();
      if (!selection || !selection.toString().length) return;
      const { startIndex, endIndex } =
        getSelectedStartEndIndex(scrollRef) || {};

      // Use VideoCreatorStore directly for clip saving
      await saveClipFromTranscript(
        videoCreator,
        {
          theme: 'social clip',
          transcriptPosition: {
            startIndex,
            endIndex,
            relevantElements: transcriptElements,
          },
        },
        {
          parallel: useNewTranscript,
        },
      );
      return;
    },
    [getSelectedStartEndIndex, videoCreator, useNewTranscript],
  );

  const handleTranscriptTextReplace = useCallback(
    (
      selections: {
        startIndex: number;
        endIndex: number;
        newValue: string;
        retainState?: boolean;
      }[],
    ) => {
      if (editorStore.useTranscriptSegments && !isSubtitlesTranslated) {
        selections.forEach(selection => {
          const segmentBoundaries = convertIndexToSegmentBoundaries(selection);
          if (segmentBoundaries == null) {
            logger.error(
              "Couldn't find segment boundaries for transcript text replace",
              selection,
            );
          } else {
            const { fromIndex, fromSegmentId, toIndex, toSegmentId } =
              segmentBoundaries;
            try {
              editorStore.editor.commandReplaceTranscriptText(
                fromSegmentId,
                fromIndex,
                toSegmentId,
                toIndex,
                selection.newValue,
              );
            } catch (err) {
              logger.error("Couldn't replace text for selection", {
                errorInfo: extractErrorInfo(err),
                segmentBoundaries,
                selection,
              });
            }
          }
        });
      } else {
        videoCreator.replaceTranscriptionElements(
          selections,
          isSubtitlesTranslated,
        );
      }
    },
    [
      editorStore.useTranscriptSegments,
      isSubtitlesTranslated,
      convertIndexToSegmentBoundaries,
    ],
  );

  return {
    selectedWordIndex,
    setSelectedWordIndex,
    compensateForRemovedElementsPosition,
    getBoundaryIndexesFromSelection,
    getStartAndEndOfSelection,
    handleCursorMove,
    handleAddPunctuation,
    handleAddKaraokeBreak,
    handleRemoveKaraokeBreak,
    handleTranscriptRestore,
    getSelectedStartEndIndex,
    handleHideKaraoke,
    handleTranscriptDelete,
    getCursorPosition,
    skipToWordWithIndex,
    getActions,
    handleRemovePhoto,
    handleAddPhoto,
    handleCutText,
    handlePasteText,
    handleSaveAsClip,
    handleTranscriptTextReplace,
  };
};
